import Papaparse from 'papaparse';
import Chart from 'chart.js/auto';
import annotationPlugin from 'chartjs-plugin-annotation';
import userCombinations from '/data/user_combinations.json'
import EventEmitter from "eventemitter3";

const initialGenerateLabel = Chart.defaults.plugins.legend.labels.generateLabels.bind();

export class AlgoBase extends EventEmitter {

  constructor(algorithmName, target, useAIToggle = true) {
    super();

    Chart.register(annotationPlugin)

    // Force integer canvas dimensions to prevent half-pixel rendering
    Chart.defaults.devicePixelRatio = Math.floor(window.devicePixelRatio) || 1; // round devicePixelRatio to avoid 1.5

    Chart.register({
      id: 'integerCanvasSize',
      beforeResize(chart, size) {
        size.width = Math.floor(size.width);
        size.height = Math.floor(size.height);
      },
      afterInit(chart) {
        const w = Math.floor(chart.width);
        const h = Math.floor(chart.height);
        if (chart.width !== w || chart.height !== h) chart.resize(w, h);
      },
      beforeUpdate(chart) {
        const w = Math.floor(chart.width);
        const h = Math.floor(chart.height);
        if (chart.width !== w || chart.height !== h) chart.resize(w, h);
      }
    });

    // Default grid
    Chart.defaults.scale.border.dash = [1, 2]

    // Default line size
    Chart.defaults.elements.line.borderWidth = 2

    // Default point visibility
    Chart.defaults.elements.point.pointStyle = false

    Chart.defaults.font.family = "Atlas Typewriter"
    Chart.defaults.color = '#000000';
    Chart.defaults.font.weight = 300
    Chart.defaults.font.size = 8

    Chart.defaults.plugins.tooltip.enabled = false
    Chart.defaults.plugins.legend.position = "bottom"
    Chart.defaults.plugins.legend.labels.boxWidth = 10
    Chart.defaults.plugins.legend.labels.boxHeight = 2
    Chart.defaults.plugins.legend.labels.usePointStyle = true
    Chart.defaults.plugins.legend.labels.padding = 25
    Chart.defaults.plugins.legend.labels.font = {...Chart.defaults.font, family: 'Chalet London Sixty', size: 8};
    Chart.defaults.plugins.legend.onClick = null

    Chart.defaults.plugins.title.position = 'left';
    Chart.defaults.plugins.title.align = 'center';
    Chart.defaults.plugins.title.fullSize = false;
    Chart.defaults.plugins.title.color = Chart.defaults.color;
    Chart.defaults.plugins.title.font = {...Chart.defaults.plugins.annotation.common.label.font};
    Chart.defaults.plugins.title.padding = {bottom: 10}

    Chart.defaults.plugins.subtitle.position = 'right';
    Chart.defaults.plugins.subtitle.align = 'center';
    Chart.defaults.plugins.subtitle.fullSize = false;
    Chart.defaults.plugins.subtitle.color = Chart.defaults.color;
    Chart.defaults.plugins.subtitle.font = {...Chart.defaults.plugins.annotation.common.label.font};
    Chart.defaults.plugins.subtitle.padding = {bottom: 10}

    // font: {family: "Atlas Typewriter", weight: 'normal'},

    // Default labels generator for legend (re-use base generateLabels())
    // https://github.com/chartjs/Chart.js/blob/a153556861074e827358446ec937555ac58c3d11/src/plugins/plugin.legend.js#L675
    Chart.defaults.plugins.legend.labels.generateLabels = (chart) => {
      // const datasets = chart.data.datasets;
      const items = initialGenerateLabel(chart);

      // Alter legend items
      for (const item of items) {
        item.lineWidth = 0
        if (item.fillStyle === "rgba(0,0,0,0.1)") item.fillStyle = item.strokeStyle
        item.pointStyle = this.generateLegendGraphic(item.fillStyle, false);
      }

      // Add legend for annotations
      if (chart.config.options.plugins.annotation) {
        for (const [key, annotation] of Object.entries(chart.config.options.plugins.annotation.annotations)) {
          if (annotation.type === "line" && annotation.display !== false && annotation.title !== undefined) {
            items.push({
              text: annotation.title,
              pointStyle: this.generateLegendGraphic(annotation.borderColor),
            })
          }
        }
      }

      return items;
    }

    Chart.defaults.plugins.annotation.common.label.font = {family: "Atlas Typewriter", weight: 300, size: 8};

    this.algorithmName = algorithmName;
    this.$widget = target;
    this.useAIToggle = useAIToggle;
    this.currentDatasetIndex = 0;
    this.aiEnabled = true;
    this.chart = null;
    this.preferredLanguage = "en"
    this.preferredUnit = "fahrenheit";

    // Remove with/without toggle when not used
    if (!this.useAIToggle) this.$widget.find(".graph-switch").remove();
  }

  setupXAxis(ticksCount) {
    // Necessary to save "this" as the callback below doesn't have the same scope
    const self = this;

    return {
      grid: {
        drawTicks: false,
      },
      ticks: {
        autoSkip: true,
        maxTicksLimit: Math.ceil(ticksCount),
        maxRotation: 45,
        minRotation: 45,
        callback: function (value, index, ticks) {
          // Format time as 5:00 am - 9:00 pm in english and 24h convertion for french 5h - 21h
          let label = this.getLabelForValue(value).toString();

          // Let's first normalize the time to 5:00 am because the received data sometimes is written as "a.m." or "AM" with or without space
          label = self.normalizeTime(label);

          //Convert to 24h format if french
          if (self.preferredLanguage === "fr") {
            const match = label.match(/^(\d+):(\d+)\s*(am|pm)$/i);
            if (match) {
              let hours = parseInt(match[1]);
              const isPM = match[3].toLowerCase() === "pm";
              if (isPM && hours !== 12) hours += 12;
              else if (!isPM && hours === 12) hours = 0;

              let minutes = match[2];
              return `${hours}h${minutes}`;
            }
          }

          return label;
        }
      },
    };
  }

  initEvents() {
    this.$widget.find(".next-dataset").on('click', (e) => {
      e.preventDefault();
      this.loadDataset((this.currentDatasetIndex + 1) % this.getLoopIndex());
    })

    if (this.useAIToggle) {
      this.$widget.find(".ai-enabled").on('change', (e) => {
        app.audio.play("audio/Toogle_AI_1.wav", 1, true)

        this.setAiEnabled(this.$widget.find(".ai-enabled").is(":checked"))
      })
    }
  }

  setAiEnabled(enabled) {
    if (!this.useAIToggle) return;

    this.aiEnabled = enabled;
    this.$widget.find(".ai-enabled").prop("checked", this.aiEnabled);
    this.loadDataset(this.currentDatasetIndex);
  }

  getLoopIndex() {
    if (this.useAIToggle) {
      return Math.floor(this.algortihmData.mainDatasets.length / 2);
    } else {
      return this.algortihmData.mainDatasets.length;
    }
  }

  setPreferredUnit(preferredUnit) {
    this.preferredUnit = preferredUnit
    this.loadDataset(this.currentDatasetIndex);
  }

  setPreferredLanguage(preferredLanguage) {
    this.preferredLanguage = preferredLanguage
    this.loadDataset(this.currentDatasetIndex);
  }

  temperatureLegend(value) {
    if (this.preferredUnit === "celsius") return value + '°C';
    else return value + '°F';
  }

  convertDatasetToPreferredUnit(dataset) {
    const celsiusDataset = []
    for (let i = 0; i < dataset.length; i++) {
      if (this.preferredUnit === "celsius") celsiusDataset.push(this.fahrenheitToCelsius(dataset[i]));
      else celsiusDataset.push(dataset[i]);
    }
    return celsiusDataset;
  }

  fahrenheitToCelsius(value) {
    return (value - 32) * (5 / 9)
  }

  normalizeTime(str) {
    return str.replace(/\s*([ap])\.?m\.?/i, (match, p) => ' ' + p.toLowerCase() + 'm');
  }

  loadDataset(datasetIndex) {
    const offset = (this.aiEnabled ? 0 : this.getLoopIndex());

    this.currentDatasetIndex = datasetIndex;

    const userCombinationData = userCombinations[this.algorithmName][datasetIndex];
    this.chart.data.datasets[0].data = this.algortihmData.mainDatasets[datasetIndex + offset];
    this.chart.update();
    this.$widget.find(".dataset-id").text(`User inputs : ${userCombinationData.title} (column ${datasetIndex + 1} / ${this.algortihmData.mainDatasets.length})`);
  }

  async buildDataset(filename, columnCountByGroup = 1) {
    const csvDataset = await this.buildDatasetFromCSV(filename, columnCountByGroup);
    // Alter data here if required
    return csvDataset;
  }

  async buildDatasetFromCSV(filename, columnCountByGroup = 1) {
    const response = await fetch(filename);
    const data = await response.text()

    const csv = Papaparse.parse(data.toString(), {
      delimiter: "",	// auto-detect
      newline: "",	// auto-detect
    });

    const labels = [];
    const flatBuild = [];

    for (const line of csv.data) {
      for (const [index, cell] of line.entries()) {
        if (index === 0) labels.push(cell);
        else {
          const floatVal = parseFloat(cell.replace(",", "."));
          if (flatBuild[index - 1] === undefined) flatBuild[index - 1] = [];
          flatBuild[index - 1].push(floatVal);
        }
      }
    }

    const allDatasets = Array.from({length: columnCountByGroup}, () => []);
    for (const [key, itemData] of Object.entries(flatBuild)) {
      allDatasets[(key % columnCountByGroup)].push(itemData);
    }

    return {labels: labels, mainDatasets: [...allDatasets[0]], allDatasets: allDatasets};
  }

  getLegendFromCMS(elementId = 0, graphId = 1) {
    const algorithmData = app.data.cms_content.aicontrol_algorithm.find((a) => a.machine_name === this.algorithmName);
    const element = algorithmData.graphs[`graph_${graphId}_legends`]
    const translatedElement = element[this.preferredLanguage] ?? element[Object.keys(element)[0]]

    return translatedElement[Math.min(translatedElement.length, Math.max(0, elementId - 1))];
  }

  generateLegendGraphic(color, useDashed = true) {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    // Set canvas size to accommodate the dashed line
    canvas.width = 18;
    canvas.height = 2;

    // Set the stroke color
    ctx.strokeStyle = color;
    ctx.lineWidth = 2;

    // Create dashed line pattern
    if (useDashed) ctx.setLineDash([2, 2, 4, 2, 2]); // 5px dash, 5px gap

    // Draw the line
    ctx.beginPath();
    ctx.moveTo(0, 1);
    ctx.lineTo(12, 1);
    ctx.stroke();

    return canvas;
  }

  drawLabelBackground(ctx, rect, backgroundColor = 'transparent', padding = 5) {
    const label = {...rect};

    // Apply padding
    label.x -= padding;
    label.width += padding * 2;
    label.y -= padding;
    label.height += padding * 2;

    ctx.save();
    ctx.translate(label.x + label.width / 2, label.y + label.height / 2);
    ctx.rotate(label.rotation * (Math.PI / 180));
    ctx.beginPath();
    ctx.moveTo(-label.width / 2, -label.height / 2);
    ctx.lineTo(label.width / 2, -label.height / 2);
    ctx.lineTo(label.width / 2, label.height / 2);
    ctx.lineTo(-label.width / 2, label.height / 2);
    ctx.strokeStyle = "transparent";
    ctx.fillStyle = backgroundColor;
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  }

  drawLineMarker(context) {
    const {x, y, x2, y2} = context.element;
    const ctx = context.chart.ctx;
    const length = 6;
    const width = 6;
    const backgroundColor = context.element.options.borderColor
    const angle = Math.atan2(y2 - y, x2 - x);
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(angle);

    // Bottom line
    ctx.beginPath();
    ctx.moveTo(0, -width);
    ctx.lineTo(length, 0);
    ctx.lineTo(0, width);
    ctx.fillStyle = backgroundColor;
    ctx.closePath();
    ctx.fill();

    // Top line
    ctx.beginPath();
    ctx.translate(y - 14, 0);
    ctx.moveTo(length, width);
    ctx.lineTo(0, 0);
    ctx.lineTo(length, -width);
    ctx.fillStyle = backgroundColor;
    ctx.closePath();
    ctx.fill();

    ctx.restore();
  }

  ready() {
    this.emit("ready");
  }

}
